/* ==========================================================================
   Markets Pages Module (100% Dynamic Multilingual Rendering across EN, CS, DE, ES, AR, PT)
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { marketTranslations } from '../locales/marketTranslations.js';
import { getSvgIcon } from '../components/Icons.js';
import { createYouTubeEmbed } from '../components/YouTubeLazyEmbed.js';

const marketMediaConfig = {
  healthcare: {
    reference: {
      img: '/images/sed-pouziti-ve-fn-plzen.jpg',
      title: {
        cs: 'Nasazení systému SED ve Fakultní nemocnici Plzeň',
        en: 'SED System Deployment at University Hospital Pilsen (FN Plzeň)',
        de: 'Einsatz des SED-Systems am Universitätsklinikum Pilsen (FN Plzeň)',
        es: 'Despliegue del sistema SED en el Hospital Universitario de Pilsen',
        pt: 'Implementação do sistema SED no Hospital Universitário de Pilsen',
        ar: 'نشر نظام SED في المستشفى الجامعي في بيلسن (FN Plzeň)'
      },
      text: {
        cs: 'Fakultní nemocnice Plzeň využívá systém Sound Event Detector k ochraně zdravotnického personálu na exponovaných pracovištích, zejména na urgentním příjmu. Systém v reálném čase upozorňuje na verbální agresi a panické výkřiky, což umožňuje bezpečnostní službě zasáhnout dříve, než dojde k fyzickému napadení personálu.',
        en: 'University Hospital Pilsen utilizes Sound Event Detector to protect medical personnel in high-risk areas, especially the Emergency Department. The system alerts in real time to verbal aggression and panic screams, allowing security teams to intervene before physical violence occurs.',
        de: 'Das Universitätsklinikum Pilsen nutzt den Sound Event Detector zum Schutz des medizinischen Personals in sensiblen Bereichen wie der Notaufnahme. Das System warnt in Echtzeit vor Aggressionen und Schreien, sodass der Sicherheitsdienst vor körperlichen Übergriffen eingreifen kann.',
        es: 'El Hospital Universitario de Pilsen utiliza Sound Event Detector para proteger al personal médico en áreas críticas, especialmente en urgencias. El sistema alerta en tiempo real sobre agresiones verbales y gritos de pánico para una intervención temprana.',
        pt: 'O Hospital Universitário de Pilsen utiliza o Sound Event Detector para proteger os profissionais de saúde em áreas críticas, especialmente no serviço de urgência. O sistema alerta em tempo real para agressões e gritos de pânico.',
        ar: 'يستخدم المستشفى الجامعي في بيلسن نظام Sound Event Detector لحماية الكوادر الطبية في أقسام الطوارئ، حيث ينبه النظام في الوقت الفعلي إلى علامات الاعتداء والصراخ للتدخل الفوري.'
      }
    },
    videos: [
      {
        id: '2sh-qBwQOG8',
        title: {
          cs: 'Ústřední vojenská nemocnice – akustická detekce v praxi',
          en: 'Central Military Hospital – Acoustic Detection in Practice',
          de: 'Zentrales Militärkrankenhaus – Akustische Detektion in der Praxis',
          es: 'Hospital Militar Central – Detección acústica en la práctica',
          pt: 'Hospital Militar Central – Deteção acústica na prática',
          ar: 'المستشفى العسكري المركزي – الكشف الصوتي في الميدان'
        },
        desc: {
          cs: 'Využití systému SED pro ochranu zdravotnického personálu a pacientů.',
          en: 'Deploying the SED system to safeguard medical personnel and patients.',
          de: 'Einsatz des SED-Systems zum Schutz von Klinikpersonal und Patienten.',
          es: 'Uso del sistema SED para proteger al personal sanitario y a los pacientes.',
          pt: 'Utilização do sistema SED para proteger profissionais de saúde e doentes.',
          ar: 'استخدام نظام SED لحماية الكوادر الطبية والمرضى.'
        }
      },
      {
        id: 'gW4Nn63EyEI',
        title: {
          cs: 'Simulace útoku a reakce bezpečnostního systému',
          en: 'Simulated Attack & Security System Response',
          de: 'Angriffssimulation & Reaktion des Sicherheitssystems',
          es: 'Simulación de ataque y respuesta del sistema de seguridad',
          pt: 'Simulação de ataque e resposta do sistema de segurança',
          ar: 'محاكاة الهجوم واستجابة النظام الأمني'
        },
        desc: {
          cs: 'Ukázka reakce systému na incident v nemocničním prostředí.',
          en: 'Demonstration of emergency workflow response during a hospital incident.',
          de: 'Demonstration des Einsatzablaufs bei einem Zwischenfall im Krankenhaus.',
          es: 'Demostración de la respuesta del protocolo ante un incidente hospitalario.',
          pt: 'Demonstração do protocolo de resposta perante um incidente hospitalar.',
          ar: 'عرض توضيحي لاستجابة النظام أثناء حادث طارئ في بيئة المستشفى.'
        }
      }
    ]
  },
  education: {
    reference: {
      img: '/images/zcu-plzen.jpg',
      title: {
        cs: 'Testování a spolupráce se Západočeskou univerzitou v Plzni',
        en: 'Testing and Collaboration with the University of West Bohemia (ZČU)',
        de: 'Erprobung und Zusammenarbeit mit der Westböhmischen Universität in Pilsen',
        es: 'Pruebas y colaboración con la Universidad de Bohemia Occidental (ZČU)',
        pt: 'Testes e colaboração com a Universidade da Boémia Ocidental (ZČU)',
        ar: 'الاختبار والتعاون مع جامعة غرب بوهيميا في بيلسن (ZČU)'
      },
      text: {
        cs: 'Ve spolupráci se Západočeskou univerzitou v Plzni probíhá testování systému SED v reálném univerzitním prostředí. Systém je optimalizován pro ochranu rozsáhlých kampusů a budov s vysokou koncentrací studentů.',
        en: 'In collaboration with the University of West Bohemia in Pilsen, SED undergoes real-world campus testing. The system is optimized for protecting expansive campuses and high-density student facilities.',
        de: 'In Zusammenarbeit mit der Westböhmischen Universität in Pilsen wird das SED-System im realen Campusbetrieb getestet und für weitläufige Universitätsareale optimiert.',
        es: 'En colaboración con la Universidad de Bohemia Occidental en Pilsen, se llevan a cabo pruebas del sistema SED en un entorno universitario real.',
        pt: 'Em colaboração com a Universidade da Boémia Ocidental em Pilsen, o sistema SED é testado em ambiente universitário real.',
        ar: 'بالتعاون مع جامعة غرب بوهيميا في بيلسن، يتم اختبار نظام SED في بيئة جامعية حقيقية لحماية الحرم الجامعي والمرافق المزدحمة.'
      }
    },
    videos: [
      {
        id: 'PBqb9XEO0lw',
        title: {
          cs: 'Jak SED detekuje střelbu a paniku: policejní cvičení na střední škole',
          en: 'How SED Detects Gunshots and Panic: Police Simulation at a High School',
          de: 'Wie SED Schüsse und Panik erkennt: Polizeiübung an einer Schule',
          es: 'Cómo detecta SED disparos y pánico: simulacro policial en un instituto',
          pt: 'Como o SED deteta tiros e pânico: simulacro policial numa escola',
          ar: 'كيف يكشف SED إطلاق النار والذعر: تدريب للشرطة في مدرسة ثانوية'
        },
        desc: {
          cs: 'Taktické cvičení aktivního střelce demonstrující okamžitou identifikaci výstřelů na chodbách školy.',
          en: 'Tactical police active shooter drill demonstrating instant gunshot identification in educational hallways.',
          de: 'Taktische Polizeiübung zur sofortigen Schusserkennung in Schulkorridoren.',
          es: 'Simulacro táctico que demuestra la identificación instantánea de disparos en pasillos escolares.',
          pt: 'Simulacro tático demonstrando identificação instantânea de disparos em corredores escolares.',
          ar: 'تدريب تكتيكي يوضح التعرف الفوري على إطلاق النار في ممرات المدارس.'
        }
      },
      {
        id: '2sh-qBwQOG8',
        title: {
          cs: 'Sound Event Detector – ochrana bezpečnosti studentů v kampusu',
          en: 'Sound Event Detector | Protecting Student Safety on Campus (TV News)',
          de: 'Sound Event Detector | Schutz der Schülersicherheit auf dem Campus',
          es: 'Sound Event Detector | Protección de la seguridad estudiantil en el campus',
          pt: 'Sound Event Detector | Proteção da segurança de estudantes no campus',
          ar: 'Sound Event Detector | حماية سلامة الطلاب في الحرم الجامعي'
        },
        desc: {
          cs: 'Televizní reportáž o akustické ochraně nasazené v univerzitním areálu.',
          en: 'Television report on SED acoustic protection deployed across educational campus grounds.',
          de: 'Fernsehbericht über den akustischen Schutz auf dem Universitätsgelände.',
          es: 'Reportaje de televisión sobre la protección acústica desplegada en recintos universitarios.',
          pt: 'Reportagem sobre a proteção acústica implementada em recintos universitários.',
          ar: 'تقرير تلفزيوني حول الحماية الصوتية المطبقة في الحرم الجامعي.'
        }
      }
    ]
  },
  industrial: {
    videos: [
      {
        id: 'wGO_nOC-2xU',
        title: {
          cs: 'SED – simulace narušení perimetru a řezání kovu',
          en: 'SED | Sabotage Demonstration | Perimeter Cut Alert',
          de: 'SED | Sabotage-Demonstration | Perimeter-Zuschnitt-Alarm',
          es: 'SED | Demostración de sabotaje | Alerta de corte perimetral',
          pt: 'SED | Demonstração de sabotagem | Alerta de corte perimetral',
          ar: 'SED | محاكاة التخريب واختراق المحيط بقطع المعادن'
        },
        desc: {
          cs: 'Ukázka okamžité detekce řezání úhlovou bruskou při pokusu o překonání bezpečnostního oplocení.',
          en: 'Field breach demonstration showing instant detection of angle grinder cutting through high-security perimeter fences.',
          de: 'Feldversuch mit sofortiger Erkennung von Trennschleifern beim Durchtrennen von Sicherheitszäunen.',
          es: 'Demostración que muestra la detección instantánea de amoladoras angulares cortando vallas de seguridad.',
          pt: 'Demonstração em campo que mostra a deteção instantânea de rebarbadoras a cortar vedações de segurança.',
          ar: 'عرض ميداني يوضح الكشف الفوري عن استخدام قاطع المعادن لقطع الأسوار الأمنية.'
        }
      },
      {
        id: 'LmROscO2oRY',
        title: {
          cs: 'Multimodální detekce dronů: fúze akustiky a videa',
          en: 'Multimodal Drone Detection: Acoustic + Video Fusion with SED & Osyfly.ai',
          de: 'Multimodale Drohnenerkennung: Akustik- und Videofusion mit SED & Osyfly.ai',
          es: 'Detección multimodal de drones: fusión acústica y de vídeo con SED y Osyfly.ai',
          pt: 'Deteção multimodal de drones: fusão acústica e de vídeo com SED e Osyfly.ai',
          ar: 'كشف الطائرات المسيرة متعدد الوسائط: دمج الصوت والفيديو مع SED و Osyfly.ai'
        },
        desc: {
          cs: 'Sledování neoprávněných dronů kombinací akustických frekvencí rotorů a optické AI detekce.',
          en: 'Autonomous UAV tracking fusing acoustic harmonics with optical AI detection.',
          de: 'Autonome Drohnenverfolgung durch Fusion akustischer Harmonien mit optischer KI-Erkennung.',
          es: 'Seguimiento autónomo de drones fusionando armónicos acústicos con detección óptica.',
          pt: 'Rastreio autónomo de drones fundindo harmónicos acústicos com deteção ótica.',
          ar: 'تتبع مستقل للطائرات المسيرة من خلال دمج الترددات الصوتية مع الكشف البصري.'
        }
      }
    ]
  },
  public: {
    videos: [
      {
        id: 'dlQyWGVT1mU',
        title: {
          cs: 'Sound Event Detector v praxi městské policie',
          en: 'Sound Event Detector in Municipal Police Operations',
          de: 'Sound Event Detector in der Praxis der Stadtpolizei',
          es: 'Sound Event Detector en la práctica de la policía municipal',
          pt: 'Sound Event Detector na prática da polícia municipal',
          ar: 'Sound Event Detector في الميدان مع شرطة البلدية'
        },
        desc: {
          cs: 'Ukázka využití akustické detekce pro zvýšení bezpečnosti ve městech.',
          en: 'Demonstration of acoustic AI detection for enhanced urban safety and quick dispatch.',
          de: 'Einsatz der akustischen Erkennung zur Erhöhung der Sicherheit im städtischen Raum.',
          es: 'Demostración del uso de la detección acústica para aumentar la seguridad en las ciudades.',
          pt: 'Demonstração do uso da deteção acústica para reforçar a segurança nas cidades.',
          ar: 'عرض توضيحي لاستخدام الكشف الصوتي لتعزيز الأمان في المدن والاستجابة السريعة.'
        }
      },
      {
        id: 'Wi0EARUqMIo',
        title: {
          cs: 'Integrace akustické detekce do městského dohledového centra',
          en: 'Integrating Acoustic Detection into Municipal Control Centers',
          de: 'Integration der akustischen Detektion in städtische Leitstellen',
          es: 'Integración de detección acústica en centros de control urbano',
          pt: 'Integração da deteção acústica em centros de controlo municipal',
          ar: 'دمج الكشف الصوتي في مراكز التحكم والمراقبة البلدية'
        },
        desc: {
          cs: 'Představení propojení systému SED s kamerovými systémy a dispečinkem.',
          en: 'Showcase of connecting the SED system directly with municipal CCTV networks and dispatch.',
          de: 'Verbindung des SED-Systems mit bestehenden städtischen Kamerasystemen und Leitstellen.',
          es: 'Presentación de la conexión del sistema SED con redes de videovigilancia y salas de control.',
          pt: 'Apresentação da ligação do sistema SED com redes de videovigilância e salas de despacho.',
          ar: 'استعراض ربط نظام SED مباشرة مع شبكات كاميرات المراقبة الحضرية وغرف العمليات.'
        }
      }
    ]
  },
  private: {
    videos: [
      {
        id: '9lt5Hz18JS4',
        title: {
          cs: 'Akustická AI detekce tříštění skla pro komerční a privátní objekty',
          en: 'AI Glass Break Detection | Sound Event Detector for Public & Private Safety',
          de: 'KI-Glasbrucherkennung | Sound Event Detector für Gewerbe- und Privatschutz',
          es: 'Detección de rotura de cristal con IA | Sound Event Detector para seguridad privada',
          pt: 'Deteção de quebra de vidro com IA | Sound Event Detector para segurança privada',
          ar: 'كشف كسر الزجاج بالذكاء الاصطناعي للمنشآت التجارية والخاصة'
        },
        desc: {
          cs: 'Okamžitá identifikace tříštění skla výloh a oken chránící nemovitosti před vloupáním.',
          en: 'High-frequency glass fracture detection protecting luxury estates, retail facades, and commercial facilities.',
          de: 'Hochfrequente Glasbrucherkennung zum Schutz von Geschäftsfassaden und Privatanwesen.',
          es: 'Detección de fractura de cristal de alta frecuencia protegiendo fincas y escaparates comerciales.',
          pt: 'Deteção de fratura de vidro de alta frequência para proteção de montras e instalações privadas.',
          ar: 'كشف تكسير الزجاج عالي التردد لحماية واجهات المتاجر والمباني التجارية والخاصة.'
        }
      }
    ]
  }
};

const marketUi = {
  en: {
    breadcrumbHome: 'Home',
    breadcrumbMarkets: 'Markets',
    consultBtn: 'Request Sector Consultation →',
    overviewEyebrow: 'SECTOR OVERVIEW & CHALLENGES',
    overviewTitle: 'Sector Overview & Tailored Protection',
    benefitsEyebrow: 'SECTOR ADVANTAGES',
    benefitsTitle: 'Key Security & Operational Benefits',
    specsEyebrow: 'TECHNICAL SPECS',
    specsTitle: 'Sector Deployment Parameters',
    privacyLabel: 'Privacy Standard',
    privacyVal: 'Privacy by Design (GDPR / HIPAA)',
    latencyLabel: 'Detection Speed',
    latencyVal: '< 5 seconds (edge AI)',
    vmsLabel: 'VMS Platforms',
    vmsVal: 'Milestone, Genetec, Hanwha, Axis',
    contactBtn: 'Contact Sector Specialist →',
    exploreEyebrow: 'TARGET SECTORS',
    exploreTitle: 'Explore Other Sectors',
    videoEyebrow: 'FIELD SIMULATIONS & MEDIA',
    videoTitle: 'Watch Live Sector Demonstrations',
    provenDeploymentEyebrow: 'PROVEN REAL-WORLD FIELD DEPLOYMENT',
    overviewMainEyebrow: 'TARGET SECTORS & MARKETS',
    overviewMainTitle: 'Protecting Key Industries & Municipal Sectors',
    overviewMainDesc: 'Sound Event Detector provides tailored acoustic AI protection for hospitals, industrial perimeters, cities, schools, and commercial properties.',
    overviewBtnDetails: 'Explore Sector →'
  },
  cs: {
    breadcrumbHome: 'Domů',
    breadcrumbMarkets: 'Trhy',
    consultBtn: 'Nezávazná konzultace pro sektor →',
    overviewEyebrow: 'VYUŽITÍ SED V PRAXI',
    overviewTitle: 'Specifické bezpečnostní výzvy a přínos SED',
    benefitsEyebrow: 'KLÍČOVÉ PŘÍNOSY PRO SEKTOR',
    benefitsTitle: 'Hlavní přínosy nasazení Sound Event Detectoru',
    specsEyebrow: 'TECHNICKÉ PARAMETRY',
    specsTitle: 'Parametry nasazení',
    privacyLabel: 'Ochrana soukromí',
    privacyVal: 'GDPR / HIPAA by Design',
    latencyLabel: 'Rychlost reakce',
    latencyVal: '< 5s (lokálně na čipu)',
    vmsLabel: 'Kompatibilní VMS',
    vmsVal: 'Milestone, Genetec, Hanwha, Axis',
    contactBtn: 'Konzultovat s odborníkem →',
    exploreEyebrow: 'CÍLOVÉ SEKTORY',
    exploreTitle: 'Další trhy a odvětví',
    videoEyebrow: 'VIDEO UKÁZKY Z PRAXE',
    videoTitle: 'Praktické ukázky nasazení v sektoru',
    provenDeploymentEyebrow: 'OVĚŘENÉ NASAZENÍ V REÁLNÉM PROVOZU',
    overviewMainEyebrow: 'CÍLOVÉ SEKTORY A TRHY',
    overviewMainTitle: 'Ochrana klíčových odvětví a veřejného prostoru',
    overviewMainDesc: 'Sound Event Detector přináší specializovanou akustickou AI ochranu pro nemocnice, průmysl, města, školy i komerční objekty.',
    overviewBtnDetails: 'Prozkoumat sektor →'
  },
  de: {
    breadcrumbHome: 'Startseite',
    breadcrumbMarkets: 'Märkte',
    consultBtn: 'Sektorkonsultation anfordern →',
    overviewEyebrow: 'SEKTORÜBERSICHT',
    overviewTitle: 'Spezifische Sicherheitsanforderungen',
    benefitsEyebrow: 'HAUPTVORTEILE',
    benefitsTitle: 'Wichtigste Vorteile für den Sektor',
    specsEyebrow: 'TECHNISCHE DATEN',
    specsTitle: 'Einsatzparameter',
    privacyLabel: 'Datenschutzstandard',
    privacyVal: 'DSGVO-konform (Privacy by Design)',
    latencyLabel: 'Erkennungszeit',
    latencyVal: '< 5 Sekunden (Edge AI)',
    vmsLabel: 'VMS-Plattformen',
    vmsVal: 'Milestone, Genetec, Hanwha, Axis',
    contactBtn: 'Spezialisten kontaktieren →',
    exploreEyebrow: 'ZIELSEKTOREN',
    exploreTitle: 'Weitere Sektoren erkunden',
    videoEyebrow: 'PRAXISVIDEOS',
    videoTitle: 'Live-Demonstrationen für den Sektor',
    provenDeploymentEyebrow: 'BEWÄHRTE PRAXISEINSÄTZE',
    overviewMainEyebrow: 'ZIELSEKTOREN & MÄRKTE',
    overviewMainTitle: 'Schutz für Schlüsselindustrien & Kommunen',
    overviewMainDesc: 'Sound Event Detector bietet maßgeschneiderten akustischen KI-Schutz für Krankenhäuser, Industrie, Städte und Schulen.',
    overviewBtnDetails: 'Sektor erkunden →'
  },
  es: {
    breadcrumbHome: 'Inicio',
    breadcrumbMarkets: 'Mercados',
    consultBtn: 'Solicitar consulta del sector →',
    overviewEyebrow: 'RESUMEN DEL SECTOR',
    overviewTitle: 'Desafíos de seguridad y protección a medida',
    benefitsEyebrow: 'VENTAJAS PRINCIPALES',
    benefitsTitle: 'Beneficios clave para el sector',
    specsEyebrow: 'ESPECIFICACIONES',
    specsTitle: 'Parámetros de despliegue',
    privacyLabel: 'Privacidad',
    privacyVal: 'Compatible con RGPD por diseño',
    latencyLabel: 'Velocidad de detección',
    latencyVal: '< 5 segundos (Edge AI)',
    vmsLabel: 'Plataformas VMS',
    vmsVal: 'Milestone, Genetec, Hanwha, Axis',
    contactBtn: 'Contactar con especialista →',
    exploreEyebrow: 'SECTORES OBJETIVO',
    exploreTitle: 'Explorar otros sectores',
    videoEyebrow: 'VIDEOS DE CAMPO',
    videoTitle: 'Demostraciones prácticas del sector',
    provenDeploymentEyebrow: 'DESPLIEGUES REALES COMPROBADOS',
    overviewMainEyebrow: 'SECTORES Y MERCADOS',
    overviewMainTitle: 'Protección para industrias y municipios',
    overviewMainDesc: 'Sound Event Detector proporciona protección acústica con IA adaptada a hospitales, industrias, ciudades y escuelas.',
    overviewBtnDetails: 'Explorar sector →'
  },
  it: {
    breadcrumbHome: 'Home',
    breadcrumbMarkets: 'Mercati',
    consultBtn: 'Richiedi consulenza di settore →',
    overviewEyebrow: 'PANORAMICA DEL SETTORE',
    overviewTitle: 'Sfide di sicurezza e protezione su misura',
    benefitsEyebrow: 'VANTAGGI DEL SETTORE',
    benefitsTitle: 'Vantaggi chiave per il settore',
    specsEyebrow: 'SPECIFICHE',
    specsTitle: 'Parametri di implementazione',
    privacyLabel: 'Privacy',
    privacyVal: 'Conforme al GDPR (Privacy by Design)',
    latencyLabel: 'Velocità di rilevamento',
    latencyVal: '< 5 secondi (Edge AI)',
    vmsLabel: 'Piattaforme VMS',
    vmsVal: 'Milestone, Genetec, Hanwha, Axis',
    contactBtn: 'Contatta uno specialista →',
    exploreEyebrow: 'SETTORI DI RIFERIMENTO',
    exploreTitle: 'Esplora altri mercati',
    videoEyebrow: 'VIDEO DAL CAMPO',
    provenDeploymentEyebrow: 'INSTALLAZIONI REALI COMPROVATE',
    overviewMainEyebrow: 'SETTORI E MERCATI',
    overviewMainTitle: 'Protezione per industrie, sanità e municipi',
    overviewMainDesc: 'Sound Event Detector offre protezione acustica con AI adattata a ospedali, industrie, città e scuole.',
    overviewBtnDetails: 'Esplora settore →'
  },
  pt: {
    breadcrumbHome: 'Início',
    breadcrumbMarkets: 'Mercados',
    consultBtn: 'Solicitar consulta do setor →',
    overviewEyebrow: 'VISÃO GERAL DO SETOR',
    overviewTitle: 'Desafios de segurança e proteção à medida',
    benefitsEyebrow: 'VANTAGENS DO SETOR',
    benefitsTitle: 'Principais benefícios para o setor',
    specsEyebrow: 'ESPECIFICAÇÕES',
    specsTitle: 'Parâmetros de implementação',
    privacyLabel: 'Privacidade',
    privacyVal: 'Conforme RGPD por design',
    latencyLabel: 'Velocidade de deteção',
    latencyVal: '< 5 segundos (Edge AI)',
    vmsLabel: 'Plataformas VMS',
    vmsVal: 'Milestone, Genetec, Hanwha, Axis',
    contactBtn: 'Contactar especialista →',
    exploreEyebrow: 'SETORES ALVO',
    exploreTitle: 'Explorar outros setores',
    videoEyebrow: 'VÍDEOS DE CAMPO',
    videoTitle: 'Demonstrações práticas do setor',
    provenDeploymentEyebrow: 'IMPLEMENTAÇÕES REAIS COMPROVADAS',
    overviewMainEyebrow: 'SETORES E MERCADOS',
    overviewMainTitle: 'Proteção para indústrias e municípios',
    overviewMainDesc: 'O Sound Event Detector oferece proteção acústica com IA para hospitais, perímetros industriais, cidades e escolas.',
    overviewBtnDetails: 'Explorar setor →'
  },
  ar: {
    breadcrumbHome: 'الرئيسية',
    breadcrumbMarkets: 'الأسواق',
    consultBtn: 'طلب استشارة للقطاع ←',
    overviewEyebrow: 'نظرة عامة على القطاع',
    overviewTitle: 'التحديات الأمنية والحماية المخصصة',
    benefitsEyebrow: 'مزايا القطاع',
    benefitsTitle: 'الفوائد الأمنية والتشغيلية الرئيسية',
    specsEyebrow: 'المواصفات الفنية',
    specsTitle: 'معايير النشر',
    privacyLabel: 'معيار الخصوصية',
    privacyVal: 'متوافق مع GDPR / HIPAA بالتصميم',
    latencyLabel: 'سرعة الكشف',
    latencyVal: 'أقل من 5 ثوانٍ (Edge AI)',
    vmsLabel: 'أنظمة VMS',
    vmsVal: 'Milestone و Genetec و Hanwha و Axis',
    contactBtn: 'تواصل مع متخصص القطاع ←',
    exploreEyebrow: 'القطاعات المستهدفة',
    exploreTitle: 'استكشف القطاعات الأخرى',
    videoEyebrow: 'العروض الميدانية والفيديوهات',
    videoTitle: 'شاهد العروض العملية المباشرة للقطاع',
    provenDeploymentEyebrow: 'عمليات نشر ميدانية واقعية ومثبتة',
    overviewMainEyebrow: 'القطاعات والأسواق المستهدفة',
    overviewMainTitle: 'حماية القطاعات الحيوية والمدن',
    overviewMainDesc: 'يوفر كاشف الأصوات SED حماية صوتية بالذكاء الاصطناعي للمستشفيات، والمناطق الصناعية، والمدن، والمدارس والمنشآت التجارية.',
    overviewBtnDetails: 'استكشف القطاع ←'
  }
};

const marketImages = {
  'healthcare': '/images/team-young-specialist-doctors-standing-corridor-hospital-1024x683.jpg',
  'education': '/images/market_education.jpg',
  'industrial': '/images/prumyslova-bezpecnost.png',
  'public': '/images/mestska-bezpecnost.png',
  'private': '/images/ochrana-komercnich-objektu.png'
};

export function renderMarketsPage(routeKey = 'markets', onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';

  if (typeof routeKey === 'function') {
    onNavigate = routeKey;
    routeKey = window.location.hash || 'markets';
  }

  let marketKey = null;
  const strRoute = String(routeKey || '');
  if (strRoute.includes('healthcare')) marketKey = 'healthcare';
  else if (strRoute.includes('education')) marketKey = 'education';
  else if (strRoute.includes('industrial')) marketKey = 'industrial';
  else if (strRoute.includes('public')) marketKey = 'public';
  else if (strRoute.includes('private')) marketKey = 'private';

  const lang = getLocale() || 'en';
  const ui = marketUi[lang] || marketUi.en;
  const langDict = marketTranslations[lang] || marketTranslations['en'] || {};
  const enDict = marketTranslations['en'] || {};

  // SUBPAGE RENDERING
  if (marketKey && (langDict[marketKey] || enDict[marketKey])) {
    const loc = langDict[marketKey] || enDict[marketKey] || {};
    const fallback = enDict[marketKey] || {};

    const meta = {
      title: loc.title || fallback.title || marketKey.toUpperCase(),
      eyebrow: loc.eyebrow || fallback.eyebrow || 'TARGET SECTOR',
      lead: loc.lead || fallback.lead || '',
      img: marketImages[marketKey] || marketImages['public'],
      overview: loc.overview || fallback.overview || '',
      benefits: loc.benefits || fallback.benefits || []
    };

    const media = marketMediaConfig[marketKey] || {};
    const refData = media.reference || null;
    const videos = (media.videos || []).map(v => ({
      id: v.id,
      title: (typeof v.title === 'object' ? (v.title[lang] || v.title.en) : v.title),
      desc: (typeof v.desc === 'object' ? (v.desc[lang] || v.desc.en) : v.desc)
    }));

    container.innerHTML = `
      <!-- Hero Header -->
      <section class="sed-section" style="background: linear-gradient(165deg, var(--navy-dark) 0%, var(--blue-dark) 100%); color: #ffffff; padding: 4.5rem 0 3.5rem;">
        <div class="sed-container">
          <div style="display: flex; gap: 0.5rem; align-items: center; font-size: 0.85rem; color: #cbd5e1; margin-bottom: 1.5rem; flex-wrap: wrap;">
            <a href="#home" style="color: #ffffff; text-decoration: none;" data-route="home">${ui.breadcrumbHome}</a> /
            <a href="#markets" style="color: #ffffff; text-decoration: none;" data-route="markets">${ui.breadcrumbMarkets}</a> /
            <span style="color: var(--blue-primary); font-weight: 700;">${t(`marketTypes.${marketKey}`)}</span>
          </div>

          <div class="sed-detail-hero-grid">
            <div>
              <div class="sed-eyebrow" style="background: rgba(255,255,255,0.1); color: var(--blue-primary); border-color: rgba(255,255,255,0.2);">${meta.eyebrow}</div>
              <h1 class="sed-page-hero-title">
                ${meta.title}
              </h1>
              <p style="font-size: 1.12rem; color: #e2e8f0; line-height: 1.65; margin-bottom: 2rem;">
                ${meta.lead}
              </p>
              <a href="#contact" class="sed-btn sed-btn-hero sed-btn-primary" data-route="contact">${ui.consultBtn}</a>
            </div>
            <div style="border-radius: var(--radius-card); overflow: hidden; border: 2px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); padding: 1.25rem; text-align: center;">
              <img src="${meta.img}" alt="${meta.title}" style="width: 100%; max-height: 340px; object-fit: cover; border-radius: 8px;">
            </div>
          </div>
        </div>
      </section>

      <!-- Main Layout with Sidebar -->
      <section class="sed-section" style="background-color: #ffffff;">
        <div class="sed-container">
          <div class="sed-detail-main-grid">
            
            <!-- Left Column: Overview & Benefits -->
            <div>
              
              <!-- Sector Overview -->
              <div style="margin-bottom: 3.5rem;">
                <div class="sed-eyebrow">${ui.overviewEyebrow}</div>
                <h2 class="sed-detail-section-title">
                  ${ui.overviewTitle}
                </h2>
                <div style="font-size: 1.05rem; color: var(--text-dark); line-height: 1.75;">
                  ${meta.overview}
                </div>
              </div>

              <!-- Single Reference Block (e.g. FN Plzeň, ZČU) -->
              ${refData ? `
                <div style="margin-bottom: 3.5rem; background: var(--blue-ultra-light); border: 1px solid var(--blue-light); border-radius: var(--radius-card); overflow: hidden; box-shadow: var(--shadow-sm);">
                  <div class="sed-split-card-grid">
                    <div style="min-height: 220px; background: #000;">
                      <img src="${refData.img}" alt="${refData.title[lang] || refData.title.en}" style="width: 100%; height: 100%; object-fit: cover; display: block;">
                    </div>
                    <div style="padding: 2rem; display: flex; flex-direction: column; justify-content: center;">
                      <div class="sed-eyebrow" style="margin-bottom: 0.5rem;">${ui.provenDeploymentEyebrow}</div>
                      <h3 class="sed-sub-title" style="margin-bottom: 0.85rem; line-height: 1.35;">
                        ${refData.title[lang] || refData.title.en}
                      </h3>
                      <p style="font-size: 0.95rem; color: var(--text-dark); line-height: 1.65; margin: 0;">
                        ${refData.text[lang] || refData.text.en}
                      </p>
                    </div>
                  </div>
                </div>
              ` : ''}

              <!-- Sector Benefits -->
              ${meta.benefits && meta.benefits.length > 0 ? `
                <div style="margin-bottom: 3.5rem;">
                  <div class="sed-eyebrow">${ui.benefitsEyebrow}</div>
                  <h2 class="sed-detail-section-title">
                    ${ui.benefitsTitle}
                  </h2>
                  <div style="display: grid; grid-template-columns: 1fr; gap: 1rem;">
                    ${meta.benefits.map(b => `
                      <div style="background: #ffffff; border: 1px solid var(--slate-line); border-left: 4px solid var(--blue-primary); border-radius: 12px; padding: 1.15rem 1.4rem; display: flex; flex-direction: row; align-items: center; gap: 1.15rem; box-shadow: var(--shadow-sm);">
                        <div style="width: 44px; height: 44px; border-radius: 10px; background: var(--blue-light); display: flex; align-items: center; justify-content: center; color: var(--blue-primary); flex-shrink: 0;">
                          ${getSvgIcon('shield', 22, 'var(--blue-primary)')}
                        </div>
                        <div style="font-size: 1.02rem; font-weight: 700; color: var(--navy-dark); line-height: 1.5; text-align: left; flex: 1;">
                          ${b}
                        </div>
                      </div>
                    `).join('')}
                  </div>
                </div>
              ` : ''}

              <!-- Related Videos -->
              ${videos && videos.length > 0 ? `
                <div style="margin-bottom: 2rem;">
                  <div class="sed-eyebrow">${ui.videoEyebrow}</div>
                  <h2 class="sed-detail-section-title">
                    ${ui.videoTitle}
                  </h2>
                  <div style="display: grid; grid-template-columns: 1fr; gap: 1.5rem;">
                    ${videos.map((v, vIdx) => `
                      <div class="sed-card" style="padding: 1.25rem; background: #ffffff; border: 1px solid var(--slate-line);">
                        <div id="market-video-slot-${vIdx}" style="margin-bottom: 1rem;"></div>
                        <h4 style="font-family: var(--font-heading); font-size: 1.05rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.35rem;">${v.title}</h4>
                        <p style="font-size: 0.9rem; color: var(--text-muted); line-height: 1.55; margin: 0;">${v.desc}</p>
                      </div>
                    `).join('')}
                  </div>
                </div>
              ` : ''}

            </div>

            <!-- Right Column: Specs & Other Markets -->
            <div class="sed-detail-sidebar">
              
              <!-- Card 1: Technical Parameters -->
              <div class="sed-card" style="background: #ffffff; border: 1px solid var(--slate-line); padding: 1.75rem; box-shadow: var(--shadow-sm);">
                <div style="font-size: 0.78rem; font-weight: 800; color: var(--blue-primary); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.3rem;">${ui.specsEyebrow}</div>
                <h3 class="sed-sidebar-title" style="margin-bottom: 1.25rem;">
                  ${ui.specsTitle}
                </h3>

                <div style="display: flex; flex-direction: column; gap: 0.85rem; font-size: 0.92rem;">
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem;">
                    <strong style="color: var(--navy-dark);">${ui.privacyLabel}</strong>
                    <span style="color: var(--blue-primary); font-weight: 800;">${ui.privacyVal}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem;">
                    <strong style="color: var(--navy-dark);">${ui.latencyLabel}</strong>
                    <span style="color: var(--navy-dark); font-weight: 600;">${ui.latencyVal}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed var(--slate-line); padding-bottom: 0.5rem; align-items: baseline;">
                    <strong style="color: var(--navy-dark);">${ui.vmsLabel}</strong>
                    <span style="text-align: right; color: var(--navy-dark); font-weight: 600;">
                      ${ui.vmsVal}
                    </span>
                  </div>
                </div>

                <a href="#contact" class="sed-btn sed-btn-primary" style="width: 100%; text-align: center; margin-top: 1.75rem; font-size: 0.88rem;" data-route="contact">${ui.contactBtn}</a>
              </div>

              <!-- Card 2: Other Markets Navigation -->
              <div class="sed-card" style="background: var(--blue-ultra-light); border: 1px solid var(--blue-light); padding: 1.75rem;">
                <div style="font-size: 0.78rem; font-weight: 800; color: var(--blue-primary); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.3rem;">
                  ${ui.exploreEyebrow}
                </div>
                <h3 class="sed-sidebar-title">
                  ${ui.exploreTitle}
                </h3>

                <div style="display: flex; flex-direction: column; gap: 0.6rem;">
                  <a href="#market-healthcare" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; flex-direction: row; align-items: center; justify-content: space-between; border-left: 3px solid var(--blue-primary); background: #ffffff;" data-route="market-healthcare">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: var(--blue-primary); display: inline-flex;">${getSvgIcon('marketHealthcare', 16, 'var(--blue-primary)')}</span>
                      ${t('marketTypes.healthcare')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#market-education" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; flex-direction: row; align-items: center; justify-content: space-between; border-left: 3px solid var(--blue-primary); background: #ffffff;" data-route="market-education">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: var(--blue-primary); display: inline-flex;">${getSvgIcon('marketEducation', 16, 'var(--blue-primary)')}</span>
                      ${t('marketTypes.education')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#market-industrial" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; flex-direction: row; align-items: center; justify-content: space-between; border-left: 3px solid var(--blue-primary); background: #ffffff;" data-route="market-industrial">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: var(--blue-primary); display: inline-flex;">${getSvgIcon('marketIndustrial', 16, 'var(--blue-primary)')}</span>
                      ${t('marketTypes.industrial')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#market-public" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; flex-direction: row; align-items: center; justify-content: space-between; border-left: 3px solid var(--blue-primary); background: #ffffff;" data-route="market-public">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: var(--blue-primary); display: inline-flex;">${getSvgIcon('marketPublic', 16, 'var(--blue-primary)')}</span>
                      ${t('marketTypes.public')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                  <a href="#market-private" class="sed-card" style="padding: 0.75rem 1rem; text-decoration: none; display: flex; flex-direction: row; align-items: center; justify-content: space-between; border-left: 3px solid var(--blue-primary); background: #ffffff;" data-route="market-private">
                    <span style="font-weight: 700; color: var(--navy-dark); font-size: 0.88rem; display: inline-flex; align-items: center; gap: 8px;">
                      <span style="color: var(--blue-primary); display: inline-flex;">${getSvgIcon('marketPrivate', 16, 'var(--blue-primary)')}</span>
                      ${t('marketTypes.private')}
                    </span>
                    <span style="color: var(--blue-primary); font-size: 0.8rem; font-weight: 800;">→</span>
                  </a>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>
    `;

    videos.forEach((v, vIdx) => {
      const slot = container.querySelector(`#market-video-slot-${vIdx}`);
      if (slot) slot.appendChild(createYouTubeEmbed(v.id, v.title));
    });

    container.querySelectorAll('[data-route]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (onNavigate) onNavigate(link.getAttribute('data-route'));
      });
    });

    return container;
  }

  // OVERVIEW PAGE (100% LOCALIZED IN DETEKCE CARD STYLE)
  const overviewTexts = {
    eyebrow: ui.overviewMainEyebrow,
    title: ui.overviewMainTitle,
    desc: ui.overviewMainDesc,
    btnDetails: ui.overviewBtnDetails
  };

  const marketCards = [
    { key: 'public', iconKey: 'marketPublic', color: '#0284c7', accent: '#0284c7' },
    { key: 'healthcare', iconKey: 'marketHealthcare', color: 'var(--blue-primary)', accent: 'var(--blue-primary)' },
    { key: 'education', iconKey: 'marketEducation', color: '#8b5cf6', accent: '#8b5cf6' },
    { key: 'industrial', iconKey: 'marketIndustrial', color: '#10b981', accent: '#10b981' },
    { key: 'private', iconKey: 'marketPrivate', color: '#f59e0b', accent: '#f59e0b' }
  ];

  container.innerHTML = `
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem;">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 840px;">
          <div class="sed-eyebrow">${overviewTexts.eyebrow}</div>
          <h1 class="sed-page-hero-title">${overviewTexts.title}</h1>
          <p class="sed-section-desc">${overviewTexts.desc}</p>
        </div>
      </div>
    </section>

    <!-- Markets Grid (5 Cards in Detekce style, Unified Height, Clean Layout) -->
    <section class="sed-section" style="background-color: #ffffff; padding-bottom: 5rem;">
      <div class="sed-container">
        <div class="sed-grid-3" style="gap: 1.75rem;">
          ${marketCards.map(item => {
            const locItem = (langDict[item.key] || enDict[item.key] || {});
            const title = t(`marketTypes.${item.key}`) || locItem.title;
            const lead = locItem.lead || '';
            const route = `market-${item.key}`;

            return `
              <div class="sed-card" style="display: flex; flex-direction: column; justify-content: space-between; min-height: 320px; background: #ffffff; border: 1.5px solid var(--slate-line); border-top: 4px solid ${item.accent}; border-radius: 14px; padding: 2rem; box-shadow: 0 4px 15px rgba(0,0,0,0.03); transition: transform 0.2s, box-shadow 0.2s;" onmouseover="this.style.transform='translateY(-4px)'; this.style.boxShadow='0 12px 28px rgba(0,0,0,0.08)';" onmouseout="this.style.transform='none'; this.style.boxShadow='0 4px 15px rgba(0,0,0,0.03)';">
                <div>
                  <div style="width: 48px; height: 48px; border-radius: 12px; background: var(--blue-ultra-light); border: 1px solid var(--blue-light); display: flex; align-items: center; justify-content: center; margin-bottom: 1.25rem;">
                    ${getSvgIcon(item.iconKey, 24, item.color)}
                  </div>
                  <h3 class="sed-card-title" style="font-size: 1.25rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.75rem; line-height: 1.35;">${title}</h3>
                  <p class="sed-card-text" style="font-size: 0.92rem; line-height: 1.6; color: var(--text-dark); margin: 0;">
                    ${lead}
                  </p>
                </div>
                <div style="margin-top: 1.75rem;">
                  <a href="#${route}" class="sed-btn sed-btn-primary" style="width: 100%; text-align: center; font-size: 0.88rem; font-weight: 700;" data-route="${route}">
                    ${overviewTexts.btnDetails}
                  </a>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    </section>
  `;

  container.querySelectorAll('[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (onNavigate) onNavigate(link.getAttribute('data-route'));
    });
  });

  return container;
}
